import { Segmented } from "@community-project-org/ui";
import type { JSX } from "react";

import type { components } from "../../generated/api.js";
import type { ScopeDraft, ScopeMode } from "./scopeDraft.js";

type ServiceGroupDTO = components["schemas"]["ServiceGroupDTO"];
type ServiceDTO = components["schemas"]["ServiceDTO"];

interface ScopePickerProps {
  value: ScopeDraft;
  onChange: (next: ScopeDraft) => void;
  groups: ServiceGroupDTO[];
  services: ServiceDTO[];
}

/**
 * Wofür eine Rolle gilt: alle Angebote, ausgewählte Gruppen und Einzelangebote, oder
 * keine. Gruppen zuerst — wer ein Festival mit vierzig Konzerten hat, gibt die Gruppe
 * frei, nicht vierzig Häkchen.
 */
export function ScopePicker({ value, onChange, groups, services }: ScopePickerProps): JSX.Element {
  const toggle = (key: "groupIds" | "serviceIds", id: string): void => {
    const current = value[key];
    onChange({
      ...value,
      [key]: current.includes(id) ? current.filter((entry) => entry !== id) : [...current, id],
    });
  };

  return (
    <div className="grid gap-3">
      <Segmented<ScopeMode>
        label="Gilt für"
        stretch
        value={value.mode}
        onChange={(mode) => onChange({ ...value, mode })}
        options={[
          { value: "ALL", label: "Alle" },
          { value: "SELECTED", label: "Auswahl" },
          { value: "NONE", label: "Keine" },
        ]}
      />

      {value.mode === "SELECTED" && (
        <div className="grid max-h-[320px] gap-3 overflow-y-auto">
          <fieldset className="grid gap-1">
            <legend className="micro pb-1">Gruppen</legend>
            {groups.length === 0 && (
              <p className="text-[13px] text-ink-3">Noch keine Gruppe. Gruppen legst du unter Angebote an.</p>
            )}
            {groups.map((group) => (
              <Check
                key={group.id}
                label={group.name}
                hint={`${group.services} Angebote`}
                checked={value.groupIds.includes(group.id)}
                onChange={() => toggle("groupIds", group.id)}
              />
            ))}
          </fieldset>
          <fieldset className="grid gap-1">
            <legend className="micro pb-1">Einzelne Angebote</legend>
            {services.map((service) => (
              <Check
                key={service.id}
                label={service.title}
                checked={value.serviceIds.includes(service.id)}
                onChange={() => toggle("serviceIds", service.id)}
              />
            ))}
          </fieldset>
        </div>
      )}

      {value.mode === "NONE" && (
        <p className="text-[13px] text-ink-2">
          Die Rolle bleibt bestehen, gilt aber für kein Angebot — etwa bis zur nächsten Saison.
        </p>
      )}
    </div>
  );
}

function Check({
  label,
  hint,
  checked,
  onChange,
}: {
  label: string;
  hint?: string;
  checked: boolean;
  onChange: () => void;
}): JSX.Element {
  return (
    <label className="flex min-h-[44px] cursor-pointer items-center gap-3 rounded px-2 hover:bg-surface-2">
      <input type="checkbox" className="h-5 w-5 accent-[var(--brand)]" checked={checked} onChange={onChange} />
      <span className="min-w-0 flex-1 truncate text-[14px] text-ink">{label}</span>
      {hint && <span className="num text-[12px] text-ink-3">{hint}</span>}
    </label>
  );
}
